import { type HsvaColor, hsvaToHex } from "@uiw/color-convert";
import Hue from "@uiw/react-color-hue";
import Saturation from "@uiw/react-color-saturation";
import type { Dispatch, SetStateAction } from "react";
import type {
  ColorAssetFormState,
  ColorPickerModalState,
} from "../hooks/useColorPickerModal";

const fieldClass =
  "min-w-0 flex-1 rounded-lg border border-black/25 bg-[var(--bg-field)] px-2.5 py-2 text-center text-sm text-[var(--text-primary)] tabular-nums outline-none focus:ring-2 focus:ring-white/25";

export function ColorPickerSliders({
  hsva,
  setHsva,
}: {
  hsva: HsvaColor;
  setHsva: Dispatch<SetStateAction<HsvaColor>>;
}) {
  return (
    <div className="mt-5 space-y-3">
      <div className="relative h-[200px] w-full overflow-hidden rounded-xl ring-1 ring-black/30">
        <Saturation
          hsva={hsva}
          radius={12}
          style={{ width: "100%", height: "100%" }}
          onChange={(next) => setHsva((prev) => ({ ...prev, ...next, a: 1 }))}
          pointer={({ left, top, color }) => (
            <div
              className="pointer-events-none absolute"
              style={{
                left,
                top,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div
                className="size-4 rounded-full border-[3px] border-white shadow-[0_1px_3px_rgba(0,0,0,0.45)]"
                style={{ backgroundColor: color }}
              />
            </div>
          )}
        />
      </div>
      <Hue
        hue={hsva.h}
        height={14}
        radius={9999}
        onChange={({ h }) =>
          setHsva((prev) => ({
            ...prev,
            h,
            a: 1,
          }))
        }
        style={{
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.35)",
        }}
      />
    </div>
  );
}

export function ColorPickerValueFields({
  hsva,
  setHexDraft,
  displayHex,
  rgba,
  applyHexString,
  onRgbChange,
}: Pick<
  ColorPickerModalState,
  | "hsva"
  | "setHexDraft"
  | "displayHex"
  | "rgba"
  | "applyHexString"
  | "onRgbChange"
>) {
  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-3">
        <span className="w-9 shrink-0 text-sm font-bold text-white">RGB</span>
        <div className="flex flex-1 gap-2">
          <input
            aria-label="Red"
            type="number"
            min={0}
            max={255}
            value={rgba.r}
            onChange={(e) => onRgbChange("r", e.target.value)}
            className={fieldClass}
          />
          <input
            aria-label="Green"
            type="number"
            min={0}
            max={255}
            value={rgba.g}
            onChange={(e) => onRgbChange("g", e.target.value)}
            className={fieldClass}
          />
          <input
            aria-label="Blue"
            type="number"
            min={0}
            max={255}
            value={rgba.b}
            onChange={(e) => onRgbChange("b", e.target.value)}
            className={fieldClass}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="w-9 shrink-0 text-sm font-bold text-white">Hex</span>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div
            className="size-9 shrink-0 rounded-md ring-1 ring-black/30"
            style={{ backgroundColor: hsvaToHex(hsva) }}
            aria-hidden="true"
          />
          <input
            aria-label="Hex color"
            type="text"
            value={displayHex}
            onChange={(e) => setHexDraft(e.target.value.toUpperCase())}
            onFocus={() => setHexDraft(hsvaToHex(hsva).toUpperCase())}
            onBlur={(e) => {
              applyHexString(e.target.value);
              setHexDraft(null);
            }}
            className={`${fieldClass} min-w-0 flex-1 font-mono text-left uppercase tracking-wide`}
          />
        </div>
      </div>
    </div>
  );
}

/** Saturation/hue sliders + hex/RGB fields, wired to the picker state. */
export function ColorPickerFields({
  pickerState,
}: {
  pickerState: ColorPickerModalState;
}) {
  return (
    <>
      <ColorPickerSliders
        hsva={pickerState.hsva}
        setHsva={pickerState.setHsva}
      />
      <ColorPickerValueFields
        hsva={pickerState.hsva}
        setHexDraft={pickerState.setHexDraft}
        displayHex={pickerState.displayHex}
        rgba={pickerState.rgba}
        applyHexString={pickerState.applyHexString}
        onRgbChange={pickerState.onRgbChange}
      />
    </>
  );
}

export function ColorAssetFormFields({
  assetForm,
}: {
  assetForm: ColorAssetFormState;
}) {
  return (
    <div className="mt-5 space-y-5">
      <div>
        <label htmlFor="asset-name" className="text-sm font-bold text-white">
          Name
        </label>
        <input
          id="asset-name"
          type="text"
          value={assetForm.name}
          onChange={(e) => assetForm.setName(e.target.value)}
          placeholder="Name this color"
          className="mt-2 w-full rounded-lg border border-black/25 bg-[var(--bg-field)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none placeholder-[var(--text-secondary)] focus:ring-2 focus:ring-white/25"
        />
      </div>
    </div>
  );
}
